"use client"

import { createContext, useContext } from "react"

const ApartContext = createContext()

export const useApartContext = () => {
  const context = useContext(ApartContext)
  if (!context) {
    throw new Error("useApartContext must be used within an ApartContextProvider")
  }
  return context
}

const ApartContextProvider = ({ children }) => {
  const apartmentData = {
    id: 8,
    titleEn: "✨Shalom Suite✨",
    titleEs: "✨Suite Shalom✨",
    titleFr: "✨Suite Shalom✨",
    titleHe: "✨סוויטת שלום✨",
    internalCode: "(BS2)",
    descriptionEn: "Spacious 1-Bedroom Apartment in Historic Nachlaot – Quiet Charm in the Heart of Jerusalem",
    descriptionEs: "Amplio apartamento de 1 dormitorio en el histórico Nachlaot – Encanto tranquilo en el corazón de Jerusalén",
    descriptionFr: "Spacieux appartement de 1 chambre dans le quartier historique de Nachlaot – Charme tranquille au cœur de Jérusalem",
    descriptionHe: "דירת חדר שינה אחת מרווחת בנחלאות ההיסטורית – שקט וקסם בלב ירושלים",
    longDescriptionEn: `Discover the perfect balance between peace and centrality in this charming 1-bedroom apartment with a large living room, located in the heart of the picturesque Nachlaot neighborhood.

Tucked away in a quiet alley, yet just a few minutes' walk from Mahane Yehuda Market, the light rail, and the city's best restaurants, cafés, and attractions – this apartment offers a truly authentic Jerusalem experience.

Enjoy the tranquil atmosphere of a historic neighborhood while staying right in the center of it all.`,
    longDescriptionEs: `Descubra el equilibrio perfecto entre paz y centralidad en este encantador apartamento de 1 dormitorio con una amplia sala de estar, ubicado en el corazón del pintoresco barrio de Nachlaot.

Escondido en un callejón tranquilo, pero a solo unos minutos a pie del Mercado Mahane Yehuda, el tren ligero y los mejores restaurantes, cafés y atracciones de la ciudad, este apartamento ofrece una experiencia verdaderamente auténtica de Jerusalén.

Disfrute de la atmósfera tranquila de un barrio histórico mientras se aloja justo en el centro de todo.`,
    longDescriptionFr: `Découvrez l'équilibre parfait entre la paix et la centralité dans ce charmant appartement de 1 chambre avec un grand salon, situé au cœur du pittoresque quartier de Nachlaot.

Niché dans une ruelle tranquille, mais à quelques minutes à pied du marché Mahane Yehuda, du tramway et des meilleurs restaurants, cafés et attractions de la ville, cet appartement offre une expérience authentique de Jérusalem.

Profitez de l'atmosphère tranquille d'un quartier historique tout en séjournant en plein centre de tout.`,
    longDescriptionHe: `גלו את האיזון המושלם בין שקט למרכזיות בדירה מקסימה זו עם חדר שינה אחד וסלון גדול, בלב שכונת נחלאות הציורית.

ממוקמת בסמטה שקטה אך במרחק הליכה קצר משוק מחנה יהודה, הרכבת הקלה והמסעדות והאטרקציות הטובות בעיר – הדירה מציעה חוויית ירושלמית אותנטית במיוחד.

תיהנו מהאווירה הרגועה של שכונה היסטורית ממש בלב העניינים.`,
    size: 35,
    guests: 2,
    bedrooms: 1,
    bathrooms: 1,
    images: [
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752966026/Apart_8_2_kpyr4x.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752966025/Apart_8_1_y9xrwi.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752966028/Apart_8_3_sqruf8.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752966029/Apart_8_4_cjaqmv.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752966031/Apart_8_5_mkzyqh.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752966032/Apart_8_6_klioz6.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752966033/Apart_8_7_l6l2iq.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752966034/Apart_8_8_caoja5.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752966035/Apart_8_9_vq2rr6.png"
    ],
    amenities: [
      { name: "Fully Equipped Kitchen", nameEs: "Cocina Totalmente Equipada", nameFr: "Cuisine Entièrement Équipée", nameHe: "מטבח מאובזר", icon: "kitchen" },
      { name: "Refrigerator", nameEs: "Refrigerador", nameFr: "Réfrigérateur", nameHe: "מקרר", icon: "fridge" },
      { name: "Oven & Stove", nameEs: "Horno y Cocina", nameFr: "Four et Cuisinière", nameHe: "תנור וכיריים", icon: "oven" },
      { name: "Microwave", nameEs: "Microondas", nameFr: "Micro-ondes", nameHe: "מיקרוגל", icon: "microwave" },
      { name: "Nespresso Machine", nameEs: "Máquina Nespresso", nameFr: "Machine Nespresso", nameHe: "מכונת נספרסו", icon: "coffee" },
      { name: "Air Conditioning", nameEs: "Aire Acondicionado", nameFr: "Climatisation", nameHe: "מיזוג אוויר", icon: "air" },
      { name: "Smart TV", nameEs: "Smart TV", nameFr: "Smart TV", nameHe: "טלוויזיה חכמה", icon: "tv" },
      { name: "High-Speed Wi-Fi", nameEs: "Wi-Fi de Alta Velocidad", nameFr: "Wi-Fi Haut Débit", nameHe: "אינטרנט אלחוטי מהיר", icon: "wifi" },
      { name: "Washing Machine", nameEs: "Lavadora", nameFr: "Machine à Laver", nameHe: "מכונת כביסה", icon: "washer" },
      { name: "Fresh Linens & Towels", nameEs: "Sábanas y Toallas Limpias", nameFr: "Draps et Serviettes Frais", nameHe: "מצעים ומגבות נקיים", icon: "bed" },
      { name: "Iron", nameEs: "Plancha", nameFr: "Fer à Repasser", nameHe: "מגהץ", icon: "iron" },
      { name: "Dining Area", nameEs: "Área de Comedor", nameFr: "Coin Repas", nameHe: "פינת אוכל", icon: "dining" },
      { name: "Cleaning Supplies", nameEs: "Artículos de Limpieza", nameFr: "Produits de Nettoyage", nameHe: "ציוד ניקיון", icon: "cleaning" }
    ],
    location: {
      addressEn: "14 Beer Sheva Street, Jerusalem",
      addressEs: "14 Calle Beer Sheva, Jerusalén",
      addressFr: "14 Rue Beer Sheva, Jérusalem",
      addressHe: "רחוב באר שבע 14, ירושלים",
      addressDescEn: "Historic Nachlaot Neighborhood, Jerusalem",
      addressDescEs: "Barrio Histórico de Nachlaot, Jerusalén",
      addressDescFr: "Quartier Historique de Nachlaot, Jérusalem",
      addressDescHe: "שכונת נחלאות ההיסטורית, ירושלים",
      coordinates: {
        lat: 31.7835,
        lng: 35.2107
      },
      highlights: [
        { nameEn: "Located in quiet and authentic Nachlaot, in a peaceful alley", nameEs: "Ubicado en el tranquilo y auténtico Nachlaot, en un callejón pacífico", nameFr: "Situé dans le quartier calme et authentique de Nachlaot, dans une ruelle paisible", nameHe: "ממוקם בנחלאות השקטה והאותנטית, בסמטה שלווה" },
        { nameEn: "3-minute walk to Mahane Yehuda Market", nameEs: "A 3 minutos a pie del Mercado Mahane Yehuda", nameFr: "3 minutes à pied du marché Mahane Yehuda", nameHe: "3 דקות הליכה לשוק מחנה יהודה" },
        { nameEn: "Close to the light rail, supermarkets, shops, restaurants", nameEs: "Cerca del tren ligero, supermercados, tiendas, restaurantes", nameFr: "Proche du tramway, des supermarchés, des magasins, des restaurants", nameHe: "קרוב לרכבת הקלה, סופרים, חנויות ומסעדות" },
        { nameEn: "Easy access to the Old City and Ben Yehuda Street", nameEs: "Fácil acceso a la Ciudad Vieja y la Calle Ben Yehuda", nameFr: "Accès facile à la Vieille Ville et à la rue Ben Yehuda", nameHe: "גישה נוחה לעיר העתיקה ולרחוב בן יהודה" },
        { nameEn: "Experience authentic Jerusalem neighborhood living", nameEs: "Experimente la vida auténtica en un barrio de Jerusalén", nameFr: "Vivez l'expérience authentique d'un quartier de Jérusalem", nameHe: "חוו את החיים בשכונה ירושלמית אותנטית" }
      ]
    },
  }

  return (
    <ApartContext.Provider value={{ apartmentData }}>
      {children}
    </ApartContext.Provider>
  )
}

export default ApartContextProvider
